/**
 * Created on Fri Jun 12 2026 4:08:23 PM
 *
 * *************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2026
 * All Rights Reserved
 *
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ************************************************************************************/


import React, { useLayoutEffect, useRef, useState } from 'react';
import { resolveSectionLayout } from './layoutResolver';
import { useElementLayout } from './useElementLayout';

// Whether a resolved section layout contains at least one ABSOLUTE-positioned entry
// (authored by the canvas builder). Only those sections get the uniform-scale stage so
// the published page reproduces the builder's design pixel-for-pixel. Legacy
// flow-relative-only layouts keep the original responsive behavior.
function hasAbsoluteEntry(resolved) {
  if (!resolved || !resolved.entries) return false;
  const ids = Object.keys(resolved.entries);
  for (let i = 0; i < ids.length; i += 1) {
    const entry = resolved.entries[ids[i]];
    if (entry && entry.geometryAbsolute === true) return true;
  }
  return false;
}

// ── Legacy (responsive) boundary: applies per-element geometry in normal flow ─────────
function LaidOutSection({ section, layoutOptions, children }) {
  const sectionRef = useRef(null);
  useElementLayout(sectionRef, section, layoutOptions);

  return (
    <div
      ref={sectionRef}
      data-template-layout-boundary="true"
      style={{ position: 'relative' }}
    >
      {children}
    </div>
  );
}

// ── Production parity boundary: a uniform-scale fixed-reference-width STAGE ────────────
//
// The builder renders the page at a fixed DESIGN width (the reference width) and then
// CSS-scales the whole thing to fit. To reproduce that EXACTLY on the published page, we
// render the section's content at the SAME reference width inside an inner stage and
// uniformly `transform: scale()` it to the available width. Because the inner content
// lays out at the reference width — identical to the builder — and the entire stage is
// scaled as one unit, EVERY detail (element positions, sizes, border-radius, fonts,
// spacing, vertical placement) matches the builder at any width. The element applier runs
// inside the stage (viewport == reference width, so geometry is applied at raw, unscaled
// coordinates) and the stage transform provides all the visual scaling.
function ScaledStageSection({ section, layoutOptions, referenceWidth, children }) {
  const outerRef = useRef(null);
  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [stageHeight, setStageHeight] = useState(null);

  // The applier measures the stage's clientWidth (== referenceWidth, unaffected by the
  // CSS transform) so geometry resolves at raw reference coordinates; the stage transform
  // then scales the result uniformly.
  useElementLayout(stageRef, section, layoutOptions);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const stage = stageRef.current;
    if (!outer || !stage || !(referenceWidth > 0)) return undefined;

    const measure = () => {
      const available = outer.clientWidth || outer.getBoundingClientRect().width || referenceWidth;
      const nextScale = available > 0 ? available / referenceWidth : 1;
      // The stage lays out at referenceWidth; its UNSCALED content height is scrollHeight.
      // The outer wrapper reserves the SCALED height so the page flows correctly below it.
      const naturalHeight = stage.scrollHeight || stage.offsetHeight || 0;
      setScale(nextScale);
      setStageHeight(naturalHeight > 0 ? naturalHeight * nextScale : null);
    };

    measure();

    let resizeObserver = null;
    let onWindowResize = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(outer);
      resizeObserver.observe(stage);
    } else if (typeof window !== 'undefined') {
      onWindowResize = measure;
      window.addEventListener('resize', onWindowResize);
    }

    // Re-measure once async content (fonts/images) settles so the reserved height and
    // scale are accurate on first paint, not just after a resize.
    let cancelled = false;
    const settle = () => { if (!cancelled) measure(); };
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(settle).catch(() => {});
    }
    if (typeof window !== 'undefined' && document.readyState !== 'complete') {
      window.addEventListener('load', settle, { once: true });
    }

    return () => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();
      if (onWindowResize) window.removeEventListener('resize', onWindowResize);
      if (typeof window !== 'undefined') window.removeEventListener('load', settle);
    };
  }, [referenceWidth]);

  return (
    <div
      ref={outerRef}
      data-template-layout-boundary="true"
      data-template-layout-stage="true"
      style={{ position: 'relative', width: '100%', overflow: 'hidden', height: stageHeight != null ? `${stageHeight}px` : undefined }}
    >
      <div
        ref={stageRef}
        data-template-layout-stage-inner="true"
        style={{ width: `${referenceWidth}px`, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'relative' }}
      >
        {children}
      </div>
    </div>
  );
}

export function SectionLayoutBoundary({ section, layoutOptions, children }) {
  const resolved = resolveSectionLayout(section);
  if (!resolved) {
    return children;
  }

  // Canvas-builder (absolute) layouts render through the uniform-scale stage for exact
  // builder/reader parity; legacy flow-relative layouts keep the responsive boundary.
  if (hasAbsoluteEntry(resolved) && resolved.referenceWidth > 0) {
    return (
      <ScaledStageSection section={section} layoutOptions={layoutOptions} referenceWidth={resolved.referenceWidth}>
        {children}
      </ScaledStageSection>
    );
  }

  return (
    <LaidOutSection section={section} layoutOptions={layoutOptions}>
      {children}
    </LaidOutSection>
  );
}

export default SectionLayoutBoundary;
